/* eslint-disable react-hooks/exhaustive-deps */
import { useQuery } from "@apollo/client";
import {
  Button,
  Container,
  Hidden,
  Typography,
  Box,
  useTheme,
} from "@mui/material";
import gql from "graphql-tag";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getTaxation } from "../../../apollo/server";
import ConfigurationContext from "../../../context/Configuration";
import UserContext from "../../../context/User";
import { useRestaurant } from "../../../hooks";
import useStyles from "./styles";
import { pushNestedSelectionLines } from "../../../utils/nestedCartLines";
import { modifierOptionId } from "../../../utils/modifierOptionId";

const TAXATION = gql`
  ${getTaxation}
`;

// Price a modifier option, honoring the group's duplicate pricing mode so the
// cart total matches the customize modal (e.g. FIRST_FREE_THEN_CHARGED).
function computeOptionPrice(basePrice, qty, addon) {
  if (qty <= 0) return 0;
  const mode = addon?.allowDuplicates ? addon?.duplicatePricingMode : null;
  if (mode === "FIRST_FREE_THEN_CHARGED") {
    return basePrice * Math.max(0, qty - 1);
  }
  if ((mode === "INCREMENTAL" || mode === "CUSTOM") && addon?.duplicatePrices) {
    const tiers = typeof addon.duplicatePrices === "string"
      ? JSON.parse(addon.duplicatePrices) : addon.duplicatePrices;
    let total = 0;
    for (let i = 1; i <= qty; i++) {
      total += resolveTierPrice(tiers[String(i)], basePrice);
    }
    return total;
  }
  return basePrice * qty;
}

// Resolve a single Nth unit's price under an INCREMENTAL tier rule (relative
// to base): { mode: SAME|FREE|PLUS, extra }. Legacy bare numbers = absolute.
function resolveTierPrice(tier, basePrice) {
  if (tier === undefined || tier === null) return basePrice;
  if (typeof tier === "number") return tier;
  if (typeof tier === "string") {
    const n = parseFloat(tier);
    return Number.isNaN(n) ? basePrice : n;
  }
  const m = tier.mode || "SAME";
  if (m === "FREE") return 0;
  if (m === "PLUS") return basePrice + (parseFloat(tier.extra) || 0);
  if (m === "MINUS") return Math.max(0, basePrice - (parseFloat(tier.extra) || 0));
  return basePrice;
}

function MRestaurantCart(props) {
  const theme = useTheme();
  const classes = useStyles();
  const navigate = useNavigate();
  const [loadingData, setLoadingData] = useState(true);
  const {
    cart,
    cartCount,
    clearCart,
    updateCart,
    isLoggedIn,
    restaurant: cartRestaurant,
    getCartMeta,
  } = useContext(UserContext);
  // A food-truck stop's cart is priced from that stop's menu.
  const cartStopId = (cartRestaurant && getCartMeta()?.stopId) || null;
  const configuration = useContext(ConfigurationContext);
  const { loading: loadingTax } = useQuery(TAXATION, {
    fetchPolicy: "network-only",
  });
  const { loading, data, error: restaurantError } = useRestaurant(cartRestaurant, undefined, cartStopId);
  const restaurantData = data?.restaurant ?? null;

  // Safety timeout — if restaurant query never resolves, unblock the UI after 8s
  useEffect(() => {
    const t = setTimeout(() => {
      if (loadingData) setLoadingData(false);
    }, 8000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (restaurantError) {
      setLoadingData(false);
      return;
    }
    if (loading) return;
    if (!restaurantData) {
      setLoadingData(false);
      return;
    }
    if (!cart || cart.length === 0) {
      setLoadingData(false);
      return;
    }
    didFocus();
    // NOTE: `cart` intentionally excluded — adding it causes infinite loop because
    // didFocus → updateCart → setCart → cart changes → re-triggers effect → loop
  }, [restaurantData, cartCount, loading, restaurantError]);

  const didFocus = async () => {
    // Check if using advanced menus
    const useAdvancedMenus = restaurantData?.advancedMenus?.some(
      (m) => m.isActive && m.items && m.items.length > 0
    );

    let foods = [];
    let addons = [];
    let options = [];

    if (useAdvancedMenus) {
      // Transform advanced menu data
      const addonsSet = new Set();
      const optionsSet = new Set();

      restaurantData.advancedMenus.forEach((menu) => {
        if (!menu.isActive) return;
        // Per-menu markup + menu-scoped id prefix — MUST mirror RestaurantDetail's
        // transform so the menu-scoped ids stored on the cart line match what we
        // rebuild here, and each menu carries its own marked-up component prices.
        const menuPct = parseFloat(menu.markupPercent || 0);
        const mk = (id) => `m${menu._id}_${id}`;
        // IMPORTANT: Apply the same filtering as RestaurantDetail.js
        const activeItems = (menu.items || []).filter(
          (i) => i.isActive && i.isAvailable !== false && !i.is86ed
        );
        activeItems.forEach((item) => {
          // Build variations from pricing (same as RestaurantDetail transformation)
          const variations = [];
          if (item.pricingMode === "VARIANTS" && item.variantPrices) {
            const entries = typeof item.variantPrices === "string"
              ? Object.entries(JSON.parse(item.variantPrices))
              : Object.entries(item.variantPrices);
            entries.forEach(([key, value]) => {
              variations.push({
                _id: `${item._id}_${key}`,
                title: key,
                price: parseFloat(value),
              });
            });
          } else {
            variations.push({
              _id: `${item._id}_default`,
              title: "Regular",
              price: parseFloat(item.basePrice || 0),
            });
          }

          // Collect foods (with same structure as RestaurantDetail). menuId +
          // markupPercent let the cart resolve the correct per-menu price when
          // the same item is on several menus with different markups.
          foods.push({
            _id: item._id.toString(),
            menuId: menu._id.toString(),
            markupPercent: menuPct,
            title: item.name,
            description: item.description || "",
            image: item.imageUrl || "",
            isActive: true,
            variations,
          });

          // Collect modifiers and modifier groups
          item.modifierGroups?.forEach((mg) => {
            const mgId = mk(mg._id.toString());
            // Collect individual modifier options first
            const optionIds = [];
            mg.modifiers?.forEach((gm) => {
              if (gm.isHidden) return;
              const mod = gm.modifier;
              if (!mod || mod.libraryStatus?.is86ed) return; // Skip 86ed (out of stock) modifiers
              const modId = modifierOptionId(mk, gm, mod._id, false);
              optionIds.push(modId);
              if (!optionsSet.has(modId)) {
                optionsSet.add(modId);
                // Calculate option price based on pricing mode
                let price = parseFloat(gm.fixedPrice || 0);
                let variantPricesMap = null;
                if (gm.priceOverrideMode === "VARIANT_BASED" && gm.variantPrices) {
                  variantPricesMap = typeof gm.variantPrices === "string"
                    ? JSON.parse(gm.variantPrices)
                    : gm.variantPrices;
                  const vals = Object.values(variantPricesMap).map((v) => parseFloat(v));
                  if (vals.length > 0) price = Math.min(...vals);
                } else if (gm.priceOverrideMode === "FREE") {
                  price = 0;
                }
                options.push({
                  _id: modId,
                  title: mod.name,
                  price,
                  variantPrices: variantPricesMap,
                });
              }
            });
            // Now add the addon with all properties
            if (!addonsSet.has(mgId)) {
              addonsSet.add(mgId);
              addons.push({
                _id: mgId,
                title: mg.name,
                description: mg.description || "",
                quantityMinimum: mg.isRequired ? (mg.minSelections || 1) : 0,
                quantityMaximum: mg.maxSelections || optionIds.length,
                options: optionIds,
                allowDuplicates: mg.allowDuplicates || false,
                maxDuplicates: mg.maxDuplicates || null,
                duplicatePricingMode: mg.duplicatePricingMode || null,
                duplicatePrices: (mg.duplicatePricingMode === 'INCREMENTAL' || mg.duplicatePricingMode === 'CUSTOM') && mg.duplicatePrices
                  ? (typeof mg.duplicatePrices === 'string' ? JSON.parse(mg.duplicatePrices) : mg.duplicatePrices)
                  : null,
                // Flat group cost, charged once when >=1 option in this group is picked.
                additionalCost: parseFloat(mg.additionalCost || 0),
              });
            }
          });

          // Collect option groups and choices
          item.optionGroups?.forEach((og) => {
            const ogId = mk(`og_${og._id}`);
            const choiceIds = [];
            og.choices?.forEach((choice, idx) => {
              const choiceId = mk(`og_${og._id}_choice_${idx}`);
              choiceIds.push(choiceId);
              if (!optionsSet.has(choiceId)) {
                optionsSet.add(choiceId);
                options.push({
                  _id: choiceId,
                  title: choice,
                  price: 0,
                });
              }
            });
            if (!addonsSet.has(ogId)) {
              addonsSet.add(ogId);
              addons.push({
                _id: ogId,
                title: og.name,
                description: "",
                quantityMinimum: og.isRequired ? 1 : 0,
                quantityMaximum: og.selectionType === "MULTI" ? choiceIds.length : 1,
                options: choiceIds,
              });
            }
          });
        });
      });

      // Merge with legacy addons/options if they exist
      if (restaurantData.addons) {
        addons = [...restaurantData.addons, ...addons];
      }
      if (restaurantData.options) {
        options = [...restaurantData.options, ...options];
      }
    } else {
      // Legacy menu system
      foods = restaurantData.categories.map((c) => c.foods.flat()).flat();
      addons = restaurantData.addons || [];
      options = restaurantData.options || [];
    }

    if (foods.length === 0) {
      setLoadingData(false);
      return;
    }

    try {
      if (cart && cartCount) {
        const transformCart = cart.map((cartItem) => {
          // Match the menu the line was ordered from FIRST — the same item id is
          // on several menus, each with its own marked-up price; matching by _id
          // alone returns the first menu's copy (e.g. the 0% Breakfast price).
          // Fall back to _id-only for legacy lines that have no menuId.
          const foodItem =
            foods.find((food) => food._id === cartItem._id && String(food.menuId) === String(cartItem.menuId))
            || foods.find((food) => food._id === cartItem._id);
          if (!foodItem) return null;
          const variationItem = foodItem.variations.find(
            (variation) => variation._id === cartItem.variation._id
          );
          if (!variationItem) return null;
          const foodItemTitle = `${foodItem.title}${
            variationItem.title ? `(${variationItem.title})` : ""
          }`;
          // Every price component already arrives marked up AND rounded per the
          // menu's rule from the backend, so the cart line is simply their sum.
          let foodItemPrice = variationItem.price;
          let optionTitles = [];
          let optionsWithPrices = [];
          if (cartItem.addons) {
            cartItem.addons.forEach((addon) => {
              const cartAddon = addons.find((add) => add._id === addon._id);
              if (!cartAddon) return null;
              const groupFee = parseFloat(cartAddon.additionalCost) || 0;
              if (groupFee > 0) foodItemPrice += groupFee;
              // Group header (name + flat fee) — mirrors the order summary layout.
              if ((addon.options || []).length > 0 || groupFee > 0) {
                optionTitles.push(cartAddon.title || 'Group');
                optionsWithPrices.push({ title: cartAddon.title || 'Group', price: groupFee > 0 ? groupFee.toFixed(2) : '', level: 0, header: true });
              }
              addon.options.forEach((option) => {
                // Item-as-modifier: the saved option carries its own price +
                // nested selections, and won't be in this component's flattened
                // `options` list — so price it directly and skip the lookup.
                if (option.isItemMember) {
                  const itemPrice = parseFloat(option.price) || 0;
                  foodItemPrice += itemPrice;
                  optionTitles.push(option.title);
                  optionsWithPrices.push({ title: `+ ${option.title}`, price: itemPrice > 0 ? itemPrice.toFixed(2) : '', level: 1 });
                  foodItemPrice += parseFloat(option.nested?.subtotal) || 0;
                  pushNestedSelectionLines(option.nested?.childSelections, optionTitles, optionsWithPrices);
                  return;
                }
                const cartOption = options.find(
                  (opt) => opt._id === option._id
                );
                if (!cartOption) return null;

                let basePrice = cartOption.price;
                if (cartOption.variantPrices && variationItem.title) {
                  const vp = cartOption.variantPrices;
                  if (vp[variationItem.title] !== undefined) {
                    basePrice = parseFloat(vp[variationItem.title]);
                  } else {
                    const key = Object.keys(vp).find(
                      (k) => k.toLowerCase() === variationItem.title.toLowerCase()
                    );
                    if (key) basePrice = parseFloat(vp[key]);
                  }
                }

                const qty = option._qty || 1;
                let optionPrice = computeOptionPrice(basePrice, qty, cartAddon);
                foodItemPrice += optionPrice;

                const optionDisplayTitle = qty > 1 ? `${cartOption.title} (x${qty})` : cartOption.title;
                optionTitles.push(optionDisplayTitle);
                optionsWithPrices.push({ title: `+ ${optionDisplayTitle}`, price: optionPrice > 0 ? optionPrice.toFixed(2) : '', level: 1 });
                // A plain modifier can carry follow-up groups of its own.
                if (option.nested?.childSelections?.length) {
                  // Charged per unit of this option, as the server does.
                  foodItemPrice += (parseFloat(option.nested.subtotal) || 0) * qty;
                  pushNestedSelectionLines(option.nested.childSelections, optionTitles, optionsWithPrices, 2, qty);
                }
              });
            });
          }
          // Components are already rounded per-component (backend) — the line is
          // their sum; the bare variation is shown as-is.
          const roundedLine = Number.isFinite(foodItemPrice) ? foodItemPrice : 0;
          return {
            ...cartItem,
            title: foodItemTitle,
            foodTitle: foodItem.title,
            variationTitle: variationItem.title,
            variationPrice: (parseFloat(variationItem.price) || 0).toFixed(2),
            optionTitles,
            optionsWithPrices,
            price: roundedLine.toFixed(2),
          };
        });
        const updatedItems = transformCart.filter((item) => item);
        if (updatedItems.length === 0) await clearCart();
        await updateCart(updatedItems);
        setLoadingData((prev) => {
          if (prev) return false;
          else return prev;
        });
        if (transformCart.length !== updatedItems.length) {
          props.showMessage({
            type: "warning",
            message: "One or more item is not available",
          });
        }
      }
    } catch (e) {
      props.showMessage({
        type: "error",
        message: e.message,
      });
    } finally {
      setLoadingData((prev) => {
        if (prev) return false;
        else return prev;
      });
    }
  };

  const calculatePrice = useMemo(
    () => () => {
      let itemTotal = 0;
      cart.forEach((cartItem) => {
        const price = parseFloat(cartItem.price);
        if (!isNaN(price)) itemTotal += price * cartItem.quantity;
      });
      return itemTotal.toFixed(2);
    },
    [cart]
  );

  const taxCalculation = useMemo(
    () => () => {
      const tax = restaurantData ? +restaurantData.tax : 0;
      if (tax === 0) {
        return tax.toFixed(2);
      }
      const amount = +calculatePrice();
      const taxAmount = ((amount / 100) * tax).toFixed(2);
      return taxAmount;
    },
    [calculatePrice, restaurantData]
  );

  const calculateTotal = useMemo(
    () => () => {
      let total = 0;
      total += +calculatePrice();
      total += +taxCalculation();
      return parseFloat(total).toFixed(2);
    },
    [calculatePrice, taxCalculation]
  );

  const handleCheckout = (e) => {
    e.preventDefault();
    // Always go to /checkout. When the user isn't logged in, that route renders
    // the login form in place and re-renders Checkout the moment the token is
    // set — so the customer lands back on checkout, not a different page.
    navigate("/checkout");
  };

  return (
    <Hidden lgUp>
      <Box
        style={{
          background: theme.palette.common.white,
          position: "fixed",
          bottom: 0,
          width: "100%",
          borderRadius: "inherit",
        }}
      >
        <Container
          maxWidth="xl"
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" style={{ flexGrow: 1 }}>
            <Box display="flex" alignItems={"center"}>
              <Typography
                style={{
                  fontWeight: 700,
                  color: theme.palette.text.secondary,
                  fontSize: "0.975rem",
                }}
              >
                {loadingData
                  ? `${configuration.currencySymbol} ...`
                  : `${configuration.currencySymbol} ${calculateTotal()}`}
              </Typography>
              <Typography
                style={{
                  ...theme.typography.caption,
                  fontWeight: 700,
                  color: theme.palette.common.black,
                  padding: "5px 10px",
                  borderRadius: 5,
                  border: "1px solid theme.palette.common.black",
                  margin: "0 8px",
                }}
                align="left"
              >
                {cartCount}
              </Typography>
            </Box>
            <Box flexGrow={1} ml={2}>
              <Button
                disabled={loadingTax}
                onClick={handleCheckout}
                style={{
                  background: theme.palette.primary.main,
                  margin: theme.spacing(2, 0),
                  borderRadius: 10,
                }}
                className={classes.checkoutContainer}
              >
                <Typography
                  style={{ color: theme.palette.common.black }}
                  className={classes.checkoutText}
                >
                  GO TO CHECKOUT
                </Typography>
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Hidden>
  );
}

export default React.memo(MRestaurantCart);
